'use client';

import { Button } from '@/components/ui/button/button';
import { cn } from '@/lib/utils';
import {
  buildFormContentFromLinkFields,
  toFormModalPayload,
  type LinkFieldsInput,
} from '@/lib/utils/form-data';
import { openFormModal } from '@/lib/utils/modal-event';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { Url } from 'next/dist/shared/lib/router/router';
import Image from 'next/image';
import Link from 'next/link';
import { useEffect, useState, type MouseEvent } from 'react';
import type { CtaType, NestedMenuLink } from '../types';
import styles from './desktopNav.module.scss';

export type DesktopNavProps = {
  menuLinks: NestedMenuLink[];
  ctas?: CtaType[];
};

const hasGrandchildren = (link: NestedMenuLink): boolean => {
  return link.children?.some((child) => child.children) ?? false;
};

const NavDropdown = ({
  link,
  isOpen,
  onClose,
}: {
  link: NestedMenuLink;
  isOpen: boolean;
  onClose: () => void;
}) => {
  const [selectedFirstLevel, setSelectedFirstLevel] = useState<string | null>(
    link.children?.[0]?.id ?? null,
  );

  useEffect(() => {
    setSelectedFirstLevel(link.children?.[0]?.id ?? null);
  }, [link.children, isOpen]);

  if (!link.children || link.children.length === 0) {
    return null;
  }

  const hasTwoLevels = hasGrandchildren(link);
  const activeFirstLevel = link.children.find((child) => child.id === selectedFirstLevel);
  const secondLevelChildren = activeFirstLevel?.children ?? [];

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={cn(styles.dropdown, {
        [styles.dropdown_open]: isOpen,
        [styles.dropdown_singleLevel]: !hasTwoLevels,
      })}
      onMouseLeave={onClose}
    >
      <div className={styles.dropdown_content}>
        {hasTwoLevels ? (
          <>
            {/* Sidebar with first-level children */}
            <div className={styles.dropdown_sidebar}>
              {link.children.map((firstLevel) => (
                <div
                  key={firstLevel.id}
                  className={cn(styles.dropdown_sidebarItem, {
                    [styles.dropdown_sidebarItem_active]: selectedFirstLevel === firstLevel.id,
                  })}
                  onMouseEnter={() => setSelectedFirstLevel(firstLevel.id)}
                >
                  {firstLevel.href ? (
                    <Link
                      href={firstLevel.href}
                      target={firstLevel.newWindow ? '_blank' : undefined}
                      rel={firstLevel.newWindow ? 'noopener noreferrer' : undefined}
                      className={styles.dropdown_sidebarItem_container}
                    >
                      <div className={styles.dropdown_sidebarItem_content_container}>
                        {firstLevel.icon?.src && (
                          <div className={styles.dropdown_sidebarItem_icon}>
                            <Image
                              src={firstLevel.icon?.src}
                              alt={firstLevel.icon?.alt ?? 'icon'}
                              width={24}
                              height={24}
                            />
                          </div>
                        )}
                        <div className={styles.dropdown_sidebarItem_content}>
                          <span className={styles.dropdown_sidebarItem_label}>
                            {firstLevel.label}
                          </span>
                          <span className={styles.dropdown_sidebarItem_subtitle}>
                            {firstLevel.subtitle}
                          </span>
                        </div>
                      </div>
                      {firstLevel.id === activeFirstLevel?.id && (
                        <div className={styles.dropdown_sidebarItem_arrow}>
                          <Image
                            src="/temp/nav/arrow-right.svg"
                            alt="icon"
                            width={20}
                            height={20}
                          />
                        </div>
                      )}
                    </Link>
                  ) : (
                    <div className={styles.dropdown_sidebarItem_container}>
                      <div className={styles.dropdown_sidebarItem_content_container}>
                        {firstLevel.icon?.src && (
                          <div className={styles.dropdown_sidebarItem_icon}>
                            <Image
                              src={firstLevel.icon?.src}
                              alt={firstLevel.icon?.alt ?? 'icon'}
                              width={24}
                              height={24}
                            />
                          </div>
                        )}
                        <div className={styles.dropdown_sidebarItem_content}>
                          <span className={styles.dropdown_sidebarItem_label}>
                            {firstLevel.label}
                            {firstLevel.id === activeFirstLevel?.id && (
                              <div className={styles.dropdown_sidebarItem_arrow}>
                                <Image
                                  src="/temp/nav/arrow-right.svg"
                                  alt="icon"
                                  width={20}
                                  height={20}
                                />
                              </div>
                            )}
                          </span>
                          <span className={styles.dropdown_sidebarItem_subtitle}>
                            {firstLevel.subtitle}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* White container with second-level children */}
            <div className={styles.dropdown_whiteContainer}>
              {secondLevelChildren.length > 0 ? (
                <div className={styles.dropdown_grid}>
                  {secondLevelChildren.map((secondLevel) => {
                    const cardContent = (
                      <>
                        {secondLevel.icon?.src && (
                          <div className={styles.dropdown_cardIcon}>
                            <Image
                              src={secondLevel.icon?.src}
                              alt={secondLevel.icon?.alt ?? 'icon'}
                              width={30}
                              height={30}
                            />
                          </div>
                        )}
                        <div className={styles.dropdown_cardContent}>
                          <h3 className={styles.dropdown_cardTitle}>{secondLevel.label}</h3>
                          <p className={styles.dropdown_cardSubtitle}>{secondLevel.subtitle}</p>
                        </div>
                      </>
                    );
                    if (!secondLevel.href) return null;
                    return (
                      <Link
                        key={secondLevel.id}
                        href={secondLevel.href}
                        target={secondLevel.newWindow ? '_blank' : undefined}
                        rel={secondLevel.newWindow ? 'noopener noreferrer' : undefined}
                        className={styles.dropdown_card}
                      >
                        {cardContent}
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className={styles.dropdown_empty}>No items available</div>
              )}
            </div>
          </>
        ) : (
          /* Single level - white container with first-level children */
          <div className={styles.dropdown_whiteContainer}>
            <div className={styles.dropdown_grid}>
              {link.children.map((child) => {
                const cardContent = (
                  <>
                    {child.icon?.src && (
                      <div className={styles.dropdown_cardIcon}>
                        <Image
                          src={child.icon.src}
                          alt={child.icon?.alt ?? 'icon'}
                          width={30}
                          height={30}
                        />
                      </div>
                    )}
                    <div className={styles.dropdown_cardContent}>
                      <h3 className={styles.dropdown_cardTitle}>{child.label}</h3>
                      <p className={styles.dropdown_cardSubtitle}>{child.subtitle}</p>
                    </div>
                  </>
                );
                if (!child.href) return null;
                return (
                  <Link
                    key={child.id}
                    href={child.href}
                    target={child.newWindow ? '_blank' : undefined}
                    rel={child.newWindow ? 'noopener noreferrer' : undefined}
                    className={styles.dropdown_card}
                  >
                    {cardContent}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const DesktopNav = ({ menuLinks, ctas }: DesktopNavProps) => {
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  return (
    <nav className={styles.desktopNav}>
      <div className={styles.desktopNav_links}>
        {menuLinks.map((link) => {
          const hasChildren = link.children && link.children.length > 0;
          const isDropdownOpen = openDropdown === link.id;

          return (
            <div
              key={link.id}
              onMouseOver={() => hasChildren && setOpenDropdown(link.id)}
              onMouseOut={() => setOpenDropdown(null)}
            >
              {hasChildren ? (
                <>
                  <button
                    type="button"
                    className={cn(styles.desktopNav_link, styles.desktopNav_linkWithDropdown, {
                      [styles.desktopNav_linkActive]: isDropdownOpen,
                    })}
                  >
                    {link.label}
                    {isDropdownOpen ? (
                      <ChevronUp className={styles.desktopNav_chevron} size={16} />
                    ) : (
                      <ChevronDown className={styles.desktopNav_chevron} size={16} />
                    )}
                  </button>
                  <NavDropdown
                    link={link}
                    isOpen={isDropdownOpen}
                    onClose={() => setOpenDropdown(null)}
                  />
                </>
              ) : link.href ? (
                <Link
                  className={styles.desktopNav_link}
                  href={link.href}
                  target={link.newWindow ? '_blank' : undefined}
                  rel={link.newWindow ? 'noopener noreferrer' : undefined}
                >
                  {link.label}
                </Link>
              ) : null}
            </div>
          );
        })}
      </div>

      <div>
        {ctas && ctas.length > 0 && (
          <div className={styles.desktopNav_ctas}>
            {/* Search commented out until implemented */}
            {/* <div className={styles.desktopNav_searchIcon}>
              <SearchIcon size={18} color="white" />
            </div> */}
            {ctas.map((cta, index) => {
              if (!cta) return null;
              const ctaFields = cta.fields as LinkFieldsInput & {
                key?: string;
                label?: string;
                url?: string;
                newWindow?: boolean;
                buttonStyle?: 'White' | 'Pink';
              };
              const ctaId = ctaFields?.key;
              const formContent = buildFormContentFromLinkFields(ctaFields);
              const formModalPayload = toFormModalPayload(formContent, { ctaId });

              const handleCtaClick = (event: MouseEvent<HTMLAnchorElement>) => {
                if (ctaFields?.destination !== 'Form') {
                  return;
                }
                if (!formModalPayload) {
                  console.warn(
                    `CTA "${ctaFields?.label ?? 'Unknown'}" has destination 'Form' but missing formId/formSlugId`,
                  );
                  return;
                }
                event.preventDefault();
                openFormModal(formModalPayload);
              };
              if (!cta.fields.url && ctaFields?.destination !== 'Form') return null;
              const ctaButton = (
                <Button
                  withArrow={!(cta.fields.buttonStyle === 'White')}
                  variant={cta.fields.buttonStyle === 'White' ? 'secondary' : 'primary'}
                >
                  {cta.fields.label}
                </Button>
              );
              if (!cta.fields.url) {
                return (
                  <button
                    key={cta.sys?.id || `cta-${index}`}
                    type="button"
                    className={styles.desktopNav_cta}
                    onClick={() => {
                      if (formModalPayload) openFormModal(formModalPayload);
                    }}
                  >
                    {ctaButton}
                  </button>
                );
              }
              return (
                <Link
                  key={cta.sys?.id || `cta-${index}`}
                  href={cta.fields.url as Url}
                  target={cta.fields.newWindow ? '_blank' : undefined}
                  rel={cta.fields.newWindow ? 'noopener noreferrer' : undefined}
                  className={styles.desktopNav_cta}
                  onClick={handleCtaClick}
                >
                  {ctaButton}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </nav>
  );
};
